import { useEffect, useState } from "react";
import * as Sentry from "@sentry/browser";
import { useHistory } from "react-router";
import { ROUTES } from "src/constants/routes";
import { getBillingV2Client } from "src/domains/billing/billingClient";
import {
  toBillingV2Trial,
  toBillingV2TrialExpiredPricing,
  type TrialExpiredPricing,
} from "src/domains/billing/trial";
import {
  trackMixpanelEvent,
  getMixpanelDaysIntoTrial,
} from "src/utils/mixpanel";
import { useAppContext } from "src/hooks/useAppContext";
import { useCampaign } from "src/billinglatest/hooks/useCampaign";
import type { WithActivateBanner } from "./withActiveBanner";

/** Where a v2 org ends its trial: the subscription management modal (GRWBKK-2365) opens on this path. */
export const BILLING_V2_MANAGE_PATH = `${ROUTES.ACCOUNT_V2}/manage`;

/**
 * The billing v2 version of `withActivateBanner`: the same props, read from the billing v2 account already in app
 * context, so a v2 org makes no billing v1 request for the banner. The trial expired modal's prices come from the
 * billing v2 catalogue, loaded only once the trial has ended.
 */
const withActivateBannerV2 =
  <T extends {}>(Component: React.ComponentType<T & WithActivateBanner>) =>
  (props: T) => {
    const history = useHistory();
    const context = useAppContext();
    const account = context.billingV2Account;
    const trial = account ? toBillingV2Trial(account) : null;
    const { isCampaignValid } = useCampaign({
      remainingTrialDays: trial?.remainingTrialDays ?? null,
    });

    const isForceTrialEnd = localStorage.getItem("isTrialEnd") === "true";
    const isShowActivateBanner =
      isForceTrialEnd ||
      (trial !== null && trial.isInTrial && !trial.hasPaymentSource);
    // Same rule as the banner: the trial expired modal only opens over a banner that shows.
    const needsTrialExpiredPricing =
      isShowActivateBanner &&
      (isForceTrialEnd || (trial?.remainingTrialDays ?? 0) <= 0);
    const tier = trial?.tier ?? null;
    const [trialExpiredPricing, setTrialExpiredPricing] =
      useState<TrialExpiredPricing | null>(null);

    useEffect(() => {
      if (!needsTrialExpiredPricing || !tier) {
        return;
      }
      let cancelled = false;
      const load = async () => {
        try {
          const catalogue = await getBillingV2Client().plans.list();
          if (!cancelled) {
            setTrialExpiredPricing(
              toBillingV2TrialExpiredPricing(catalogue, tier),
            );
          }
        } catch (error) {
          // No prices, no modal, as v1 does for a currency it holds no prices for.
          Sentry.captureException(error);
        }
      };
      void load();
      return () => {
        cancelled = true;
      };
    }, [needsTrialExpiredPricing, tier]);

    // Not loaded yet, or billing v2 failed: show nothing, as v1 does.
    if (!trial) {
      return null;
    }

    const onClickActivateBanner = () => {
      history.push(BILLING_V2_MANAGE_PATH);

      trackMixpanelEvent({
        event: isCampaignValid
          ? "Coupon Banner Clicked"
          : "Activate Banner Clicked",
        context,
      });
      trackMixpanelEvent({
        event: "Upgrade Initiated",
        context,
        properties: {
          days_into_trial: getMixpanelDaysIntoTrial(context),
          source: isCampaignValid ? "coupon_banner" : "activate_banner",
        },
      });
    };

    return (
      <Component
        {...props}
        isShowActivateBanner={isShowActivateBanner}
        remainingTrialDays={trial.remainingTrialDays}
        onClickActivateBanner={onClickActivateBanner}
        planTier={tier}
        isCorePlan={tier === "core"}
        trialExpiredPricing={trialExpiredPricing}
        managePath={BILLING_V2_MANAGE_PATH}
      />
    );
  };

export default withActivateBannerV2;
